/**
 * Размеры картинок, которые умеет отдавать прокси сервера.
 *
 * Постеры и кадры витрины идут через `/api/v1/images/<источник>/<размер>/<файл>`,
 * и размер в адресе — один из заранее известных (`w92`…`w780`, `original`).
 * Значит, клиент может сам попросить копию нужной ширины, а не тянуть постер на
 * 500 точек в карточку шириной 180. У картинок других источников (медиатека,
 * логотипы каналов) размера в адресе нет — для них вариантов не бывает, и
 * функции честно отвечают `null`.
 */

const SIZED = /(\/images\/(?:tmdb|demo)\/)(w\d+|original)(\/)/;

export type ImageWidth = 'w92' | 'w154' | 'w185' | 'w342' | 'w500' | 'w780' | 'w1280';

/** Тот же файл в другой ширине; `null` — у адреса нет размера. */
export function imageVariant(url: string | null | undefined, size: ImageWidth): string | null {
  if (!url || !SIZED.test(url)) return null;
  return url.replace(SIZED, `$1${size}$3`);
}

/**
 * `srcset` постера: три ширины, из которых браузер выберет по размеру карточки
 * и плотности экрана. Без вариантов — `undefined`, и остаётся просто `src`.
 */
export function posterSrcSet(url: string | null | undefined): string | undefined {
  const small = imageVariant(url, 'w185');
  const medium = imageVariant(url, 'w342');
  const large = imageVariant(url, 'w500');
  if (small === null || medium === null || large === null) return undefined;
  return `${small} 185w, ${medium} 342w, ${large} 500w`;
}

/** Самая маленькая копия — для цвета постера и «размытой» подложки. */
export function tinyImage(url: string | null | undefined): string | null {
  if (!url) return null;
  return imageVariant(url, 'w92') ?? url;
}

/**
 * Копия для размытого фона (страница тайтла и её шаги): кадр в 780 точек,
 * постер — в 342. Размывается один раз на маленьком холсте (`soft-image.ts`,
 * вид `light`) — без `filter: blur` на большом слое.
 *
 * Правило владельца: фон размыт везде, кроме слайдера главной, но в нём
 * должны проступать образы — силуэты, свет, акценты (05.10.2026). Копия в 185
 * точек для этого мала: её квадраты сжатия прячет только сильное размытие, а
 * после него «ничего не разобрать». Крупная копия при уменьшении теряет
 * квадраты сама, и размыть её можно слегка.
 */
export function washImage(
  backdrop: string | null | undefined,
  poster?: string | null,
): string | null {
  if (backdrop) return imageVariant(backdrop, 'w780') ?? backdrop;
  // Постер — в 500 точек: при лёгком размытии копия в 342 точки уменьшается
  // слишком мало, и квадраты сжатия могли бы проступить.
  if (poster) return imageVariant(poster, 'w500') ?? poster;
  return null;
}

/**
 * ЧЁТКИЙ кадр слайдера главной в ширине по экрану: 1280 точек, если кадр на
 * экране шире ~900 физических пикселей, иначе 780. Выбор явный, а не через
 * `srcset`: сцена заранее декодирует ровно ту картинку, что встанет на экран,
 * и растворение не уходит в недогруженный кадр.
 */
export function stageArtImage(url: string | null | undefined, screenWidth: number, density: number): string | null {
  if (!url) return null;
  // Кадр занимает ~¾ ширины, на телефоне (≤768) — всю.
  const shown = screenWidth * (screenWidth <= 768 ? 1 : 0.76) * Math.max(1, density);
  return imageVariant(url, shown > 900 ? 'w1280' : 'w780') ?? url;
}
